import type { AvatarRootProps } from '@chakra-ui/react';
import VAvatar from '@voly/ui-components/src/v2/VAvatar.jsx';
import VAvatarGroup from '@voly/ui-components/src/v2/VAvatarGroup';
import VBox from '@voly/ui-components/src/v2/VBox.jsx';
import VButton from '@voly/ui-components/src/v2/VButton.jsx';
import VFormField from '@voly/ui-components/src/v2/VFormField.jsx';
import VIconButton from '@voly/ui-components/src/v2/VIconButton.jsx';
import VMaterialIcon from '@voly/ui-components/src/v2/icons/VMaterialIcon.jsx';
import VPopover from '@voly/ui-components/src/v2/VPopover.jsx';
import VMultiSelect from '@voly/ui-components/src/v2/VMultiSelect.jsx';
import VStack from '@voly/ui-components/src/v2/VStack.jsx';
import VStackRow from '@voly/ui-components/src/v2/VStackRow.jsx';
import VText from '@voly/ui-components/src/v2/VText.jsx';
import VTooltip from '@voly/ui-components/src/v2/VTooltip.jsx';
import { ReactElement, useState } from 'react';
import { translate } from 'src/app/project/features/translate/translate';
import { TEditablePointField } from './editablePointField';

export type TPointAssignee = {
  value: string;
  label: string;
  avatarUrl?: string;
};

type TPointAssigneesProps = {
  assignees: TEditablePointField<TPointAssignee[]> | undefined;
  options: TPointAssignee[];
};

const POPOVER_WIDTH = '20rem';
const MAX_VISIBLE_ASSIGNEES = 5;

const uniqueByValue = (assignees: TPointAssignee[]): TPointAssignee[] =>
  assignees.filter(
    (assignee, index) => assignees.findIndex((other) => other.value === assignee.value) === index,
  );

const toValues = (assignees: TPointAssignee[]): string[] =>
  assignees.map((assignee) => assignee.value);

const haveSameValues = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((value) => right.includes(value));

const AssigneeOptionLabel = ({ option }: { option: TPointAssignee }): ReactElement => (
  <VStackRow gap={2}>
    <VAvatar
      size="2xs"
      name={option.label}
      src={option.avatarUrl}
      aria-hidden="true"
      children={undefined}
    />
    <VText as="span">{option.label}</VText>
  </VStackRow>
);

const formatAssigneeOption = (option: TPointAssignee): ReactElement => (
  <AssigneeOptionLabel option={option} />
);

type TStyleFn = (provided: Record<string, unknown>) => Record<string, unknown>;

const INLINE_MENU_STYLES: Record<string, TStyleFn> = {
  dropdownIndicator: () => ({ display: 'none' }),
  menu: (provided) => ({
    ...provided,
    position: 'static',
    marginTop: 2,
    marginBottom: 0,
  }),
  menuList: (provided) => ({
    ...provided,
    boxShadow: 'none',
    borderRadius: 0,
    padding: 0,
  }),
};

type TAvatarGroupItemProps = Omit<AvatarRootProps, 'children'>;

type TAssigneeDetailsProps = {
  assignee: TPointAssignee;
  canEdit: boolean;
  onRemove: () => void;
};

const AssigneeDetails = ({ assignee, canEdit, onRemove }: TAssigneeDetailsProps): ReactElement => (
  <VStack gap={2} alignItems="stretch">
    <AssigneeOptionLabel option={assignee} />
    {canEdit ? (
      <VButton color="secondary" size="small" onClick={onRemove}>
        {translate('remove_from_point')}
      </VButton>
    ) : null}
  </VStack>
);

type TAssigneeAvatarProps = {
  assignee: TPointAssignee;
  canEdit: boolean;
  onRemove: (assignee: TPointAssignee) => void;
} & TAvatarGroupItemProps;

const AssigneeAvatar = ({
  assignee,
  canEdit,
  onRemove,
  ...groupItemProps
}: TAssigneeAvatarProps): ReactElement => {
  const [open, setOpen] = useState(false);

  const handleRemove = (): void => {
    setOpen(false);
    onRemove(assignee);
  };

  return (
    <VPopover
      open={open}
      onOpenChange={({ open: nextOpen }: { open: boolean }) => setOpen(nextOpen)}
      showArrow={false}
      title={undefined}
      footer={undefined}
      positioning={undefined}
      defaultOpen={undefined}
      width={undefined}
      unmountOnExit
      trigger={
        <VTooltip content={assignee.label}>
          <VBox display="inline-flex" cursor="pointer">
            <VAvatar
              {...groupItemProps}
              name={assignee.label}
              src={assignee.avatarUrl}
              children={undefined}
            />
          </VBox>
        </VTooltip>
      }
      body={<AssigneeDetails assignee={assignee} canEdit={canEdit} onRemove={handleRemove} />}
    />
  );
};

const HiddenAssigneesAvatar = ({
  assignees,
  ...groupItemProps
}: { assignees: TPointAssignee[] } & TAvatarGroupItemProps): ReactElement => (
  <VTooltip
    content={
      <VStack gap={1} alignItems="stretch">
        {assignees.map((assignee) => (
          <VText key={assignee.value}>{assignee.label}</VText>
        ))}
      </VStack>
    }
  >
    <VBox display="inline-flex">
      <VAvatar
        {...groupItemProps}
        name={undefined}
        src={undefined}
        fontSize="sm"
        fontWeight="semibold"
      >
        +{assignees.length}
      </VAvatar>
    </VBox>
  </VTooltip>
);

const AssigneeAvatars = ({
  assignees,
}: {
  assignees: TEditablePointField<TPointAssignee[]>;
}): ReactElement | null => {
  if (assignees.value.length === 0) {
    return null;
  }

  const visibleAssignees = assignees.value.slice(0, MAX_VISIBLE_ASSIGNEES);
  const hiddenAssignees = assignees.value.slice(MAX_VISIBLE_ASSIGNEES);

  const removeAssignee = (removed: TPointAssignee): void => {
    assignees.onSave(assignees.value.filter((assignee) => assignee.value !== removed.value));
  };

  return (
    <VAvatarGroup size="lg" spaceX="-2">
      {visibleAssignees.map((assignee) => (
        <AssigneeAvatar
          key={assignee.value}
          assignee={assignee}
          canEdit={assignees.canEdit}
          onRemove={removeAssignee}
        />
      ))}
      {hiddenAssignees.length > 0 ? <HiddenAssigneesAvatar assignees={hiddenAssignees} /> : null}
    </VAvatarGroup>
  );
};

type TAssigneePickerProps = {
  assignees: TEditablePointField<TPointAssignee[]>;
  options: TPointAssignee[];
};

const AssigneePicker = ({ assignees, options }: TAssigneePickerProps): ReactElement => {
  const [open, setOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>(() => toValues(assignees.value));

  const choices = uniqueByValue([...assignees.value, ...options]);

  const handleOpenChange = ({ open: nextOpen }: { open: boolean }): void => {
    setOpen(nextOpen);

    if (nextOpen) {
      setSelectedValues(toValues(assignees.value));

      return;
    }

    if (haveSameValues(selectedValues, toValues(assignees.value))) {
      return;
    }

    assignees.onSave(choices.filter((choice) => selectedValues.includes(choice.value)));
  };

  return (
    <VPopover
      open={open}
      onOpenChange={handleOpenChange}
      width={POPOVER_WIDTH}
      showArrow={false}
      title={undefined}
      footer={undefined}
      positioning={undefined}
      defaultOpen={undefined}
      unmountOnExit
      trigger={
        <VIconButton
          aria-label={translate('add_assignees')}
          variant="outline"
          colorPalette="gray"
          size="xs"
        >
          <VMaterialIcon name="add" size="md" />
        </VIconButton>
      }
      body={
        <VMultiSelect
          autoFocus
          menuIsOpen
          showSelectedOptions
          controlShouldRenderValue={false}
          isClearable={false}
          clearableSearch
          groupBy={undefined}
          placeholder={translate('search_users')}
          collection={{ items: choices }}
          value={selectedValues}
          onValueChange={({ value }: { value: string[] }) => setSelectedValues(value)}
          formatOptionLabel={formatAssigneeOption}
          chakraStyles={INLINE_MENU_STYLES}
        />
      }
    />
  );
};

const AssigneesAction = ({ assignees, options }: TAssigneePickerProps): ReactElement | null => {
  if (assignees.canEdit) {
    return <AssigneePicker assignees={assignees} options={options} />;
  }

  if (assignees.value.length === 0) {
    return (
      <VText textStyle="sm" color="fg.muted">
        {translate('none')}
      </VText>
    );
  }

  return null;
};

const PointAssignees = ({ assignees, options }: TPointAssigneesProps): ReactElement | null => {
  if (!assignees) {
    return null;
  }

  return (
    <VFormField
      data-testid="point-assignees"
      label={translate('assignees')}
      helperText={undefined}
      errorText={undefined}
      invalid={undefined}
      readOnly={!assignees.canEdit}
      gap={2}
    >
      <VStackRow gap={2}>
        <AssigneeAvatars assignees={assignees} />
        <AssigneesAction assignees={assignees} options={options} />
      </VStackRow>
    </VFormField>
  );
};

export default PointAssignees;
